import { Check, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type {
	LLMEvaluation,
	LLMTrace,
} from '../../ai-tracer-integration/types/ai-tracer-types';
import { formatContentForDisplay } from '../utils/parse-json-or-properties';

interface Tab {
	id: string;
	label: string;
	content: React.ReactNode;
}

interface TabbedDetailModalProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	tabs: Tab[];
	navigationHeader?: React.ReactNode;
}

// Copy to clipboard utility component
const CopyButton = ({ text, label }: { text: string; label?: string }) => {
	const [copied, setCopied] = useState(false);

	const handleCopy = async (e: React.MouseEvent) => {
		e.stopPropagation();
		try {
			await navigator.clipboard.writeText(text);
			setCopied(true);
			setTimeout(() => setCopied(false), 2000);
		} catch (err) {
			console.error('Failed to copy:', err);
		}
	};

	return (
		<button
			type='button'
			onClick={handleCopy}
			className='tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors'
			title={`Copy ${label || 'text'}`}
		>
			{copied ? (
				<>
					<Check className='tw-h-3 tw-w-3 tw-text-green-600' />
					<span className='tw-text-green-600'>Copied!</span>
				</>
			) : (
				<>
					<Copy className='tw-h-3 tw-w-3' />
					<span>Copy</span>
				</>
			)}
		</button>
	);
};

export const TabbedDetailModal = ({
	isOpen,
	onClose,
	title,
	tabs,
	navigationHeader,
}: TabbedDetailModalProps) => {
	const [activeTab, setActiveTab] = useState('');
	const modalContentRef = useRef<HTMLDivElement>(null);

	// Reset active tab when tabs change or modal opens
	useEffect(() => {
		if (isOpen && tabs.length > 0) {
			setActiveTab(tabs[0].id);
		}
	}, [isOpen, tabs]);

	// Prevent body scroll when modal is open
	useEffect(() => {
		if (isOpen) {
			// Prevent scrolling on the body
			document.body.style.overflow = 'hidden';
			return () => {
				// Restore scrolling when modal closes
				document.body.style.overflow = '';
			};
		}
	}, [isOpen]);

	// Handle click outside to close modal
	const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
		// Only close if clicking the overlay background, not the modal content
		if (
			modalContentRef.current &&
			!modalContentRef.current.contains(e.target as Node)
		) {
			onClose();
		}
	};

	if (!isOpen || tabs.length === 0) return null;

	const activeTabContent = tabs.find((tab) => tab.id === activeTab)?.content;
	const showTabs = tabs.length > 1;

	return (
		<div
			onClick={handleOverlayClick}
			style={{
				position: 'fixed',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				width: '100vw',
				height: '100vh',
				backgroundColor: 'rgba(0, 0, 0, 0.5)',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				zIndex: 50,
				margin: 0,
				padding: 0,
			}}
		>
			<div
				ref={modalContentRef}
				className='tw-bg-white tw-rounded-lg tw-max-w-6xl tw-max-h-[90vh] tw-w-full tw-mx-4 tw-flex tw-flex-col'
				style={{
					scrollbarWidth: 'thin',
					scrollbarColor: '#cbd5e1 #f1f5f9',
				}}
			>
				{/* Header */}
				<div className='tw-flex tw-justify-between tw-items-center tw-p-6 tw-border-b'>
					<h3 className='tw-text-lg tw-font-semibold'>{title}</h3>
					<button
						type='button'
						onClick={onClose}
						className='tw-text-gray-500 hover:tw-text-gray-700 tw-text-xl tw-leading-none'
					>
						✕
					</button>
				</div>

				{/* Navigation Header - optional */}
				{navigationHeader && navigationHeader}

				{/* Tabs - only show if more than one tab */}
				{showTabs && (
					<div className='tw-border-b tw-border-gray-200'>
						<nav className='tw-flex tw-space-x-8 tw-px-6' aria-label='Tabs'>
							{tabs.map((tab) => (
								<button
									key={tab.id}
									type='button'
									onClick={() => setActiveTab(tab.id)}
									className={`tw-whitespace-nowrap tw-py-4 tw-px-1 tw-border-b-2 tw-font-medium tw-text-sm ${
										activeTab === tab.id
											? 'tw-border-blue-500 tw-text-blue-600'
											: 'tw-border-transparent tw-text-gray-500 hover:tw-text-gray-700 hover:tw-border-gray-300'
									}`}
								>
									{tab.label}
								</button>
							))}
						</nav>
					</div>
				)}

				{/* Content */}
				<div className='tw-flex-1 tw-overflow-y-auto tw-p-6'>
					{activeTabContent}
				</div>
			</div>
		</div>
	);
};

interface EvaluationTabContentProps {
	evaluation: LLMEvaluation;
}

export const EvaluationTabContent = ({
	evaluation,
}: EvaluationTabContentProps) => {
	const MAX_SCORE = 10;

	const getScoreColor = (score: number) => {
		if (score >= 8) return 'tw-text-green-600';
		if (score >= 6) return 'tw-text-yellow-600';
		return 'tw-text-red-600';
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleString();
	};

	return (
		<div className='tw-space-y-4'>
			<div className='tw-grid tw-grid-cols-2 tw-gap-4'>
				<div>
					<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
						<h4 className='tw-font-medium tw-text-gray-700'>Trace ID</h4>
						<CopyButton text={evaluation.trace_id} label='Trace ID' />
					</div>
					<div className='tw-text-sm tw-font-mono'>{evaluation.trace_id}</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Category</h4>
					<div className='tw-text-sm'>{evaluation.category}</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>
						Overall Score
					</h4>
					<div
						className={`tw-text-lg tw-font-bold ${getScoreColor(evaluation.overall_score)}`}
					>
						{evaluation.overall_score.toFixed(1)}/{MAX_SCORE}
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Timestamp</h4>
					<div className='tw-text-sm'>{formatDate(evaluation.timestamp)}</div>
				</div>
			</div>
			<div>
				<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>
					Category Scores
				</h4>
				<div className='tw-grid tw-grid-cols-2 tw-gap-4'>
					<div className='tw-bg-gray-50 tw-p-3 tw-rounded'>
						<div className='tw-flex tw-justify-between tw-items-center'>
							<span className='tw-font-medium'>Accuracy</span>
							<span
								className={`tw-font-bold ${getScoreColor(evaluation.categories.accuracy)}`}
							>
								{evaluation.categories.accuracy.toFixed(1)}/{MAX_SCORE}
							</span>
						</div>
					</div>
					<div className='tw-bg-gray-50 tw-p-3 tw-rounded'>
						<div className='tw-flex tw-justify-between tw-items-center'>
							<span className='tw-font-medium'>Relevance</span>
							<span
								className={`tw-font-bold ${getScoreColor(evaluation.categories.relevance)}`}
							>
								{evaluation.categories.relevance.toFixed(1)}/{MAX_SCORE}
							</span>
						</div>
					</div>
					<div className='tw-bg-gray-50 tw-p-3 tw-rounded'>
						<div className='tw-flex tw-justify-between tw-items-center'>
							<span className='tw-font-medium'>Coherence</span>
							<span
								className={`tw-font-bold ${getScoreColor(evaluation.categories.coherence)}`}
							>
								{evaluation.categories.coherence.toFixed(1)}/{MAX_SCORE}
							</span>
						</div>
					</div>
					<div className='tw-bg-gray-50 tw-p-3 tw-rounded'>
						<div className='tw-flex tw-justify-between tw-items-center'>
							<span className='tw-font-medium'>Safety</span>
							<span
								className={`tw-font-bold ${getScoreColor(evaluation.categories.safety)}`}
							>
								{evaluation.categories.safety.toFixed(1)}/{MAX_SCORE}
							</span>
						</div>
					</div>
				</div>
			</div>
			{evaluation.feedback && (
				<div>
					<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
						<h4 className='tw-font-medium tw-text-gray-700'>Feedback</h4>
						<CopyButton text={evaluation.feedback} label='Feedback' />
					</div>
					<div className='tw-bg-blue-50 tw-rounded tw-text-sm tw-border tw-border-blue-200 tw-h-32'>
						<div
							className='tw-overflow-auto tw-p-3 tw-h-full'
							style={{
								scrollbarWidth: 'thin',
								scrollbarColor: '#cbd5e1 #f1f5f9',
							}}
						>
							{(() => {
								const formatted = formatContentForDisplay(evaluation.feedback);
								return (
									<pre
										className={`tw-whitespace-pre tw-text-xs tw-min-w-0 ${formatted.isParsed ? '' : 'tw-font-mono'}`}
									>
										{formatted.content}
									</pre>
								);
							})()}
						</div>
					</div>
				</div>
			)}
			{evaluation.metadata && (
				<div>
					<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
						<h4 className='tw-font-medium tw-text-gray-700'>Metadata</h4>
						<CopyButton
							text={JSON.stringify(evaluation.metadata)}
							label='Metadata'
						/>
					</div>
					{(() => {
						const formatted = formatContentForDisplay(evaluation.metadata);
						return (
							<pre
								className={`tw-bg-gray-100 tw-p-3 tw-rounded tw-text-sm tw-overflow-x-auto tw-whitespace-pre tw-min-w-0 tw-max-w-full ${formatted.isParsed ? '' : 'tw-font-mono'}`}
							>
								{formatted.content}
							</pre>
						);
					})()}
				</div>
			)}
		</div>
	);
};

interface TraceTabContentProps {
	trace: LLMTrace;
	homeInfo?: {
		canGoHome: boolean;
		homeLabel: string;
		homeTitle: string;
	};
}

export const TraceTabContent = ({ trace, homeInfo }: TraceTabContentProps) => {
	const [isSystemPromptExpanded, setIsSystemPromptExpanded] = useState(false);

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleString();
	};

	const formatModelName = (trace: LLMTrace) => {
		// First try to get model name from metadata
		if (trace.metadata && typeof trace.metadata === 'object') {
			const metadata = trace.metadata as Record<string, unknown>;
			if (metadata.modelId && typeof metadata.modelId === 'string') {
				// Extract the model name from modelId like "anthropic.claude-3-5-haiku-20241022-v1:0"
				if (metadata.modelId.includes('anthropic.')) {
					return metadata.modelId.replace('anthropic.', '');
				}
				return metadata.modelId;
			}
		}

		// Fallback to parsing model_name
		if (trace.model_name.includes('arn:aws:bedrock')) {
			return trace.model_name.split('/').pop() || trace.model_name;
		}
		if (trace.model_name.includes('anthropic.')) {
			return trace.model_name.replace('anthropic.', '');
		}
		return trace.model_name;
	};

	const getAppName = (trace: LLMTrace) => {
		if (trace.metadata && typeof trace.metadata === 'object') {
			const metadata = trace.metadata as Record<string, unknown>;
			return typeof metadata.app === 'string' ? metadata.app : 'Unknown';
		}
		return 'Unknown';
	};

	// Simple orphan check: has parent_span_id but navigation shows "Only Trace"
	const isOrphan = trace.parent_span_id && homeInfo?.homeLabel === 'Only Trace';

	return (
		<div className='tw-space-y-4'>
			{trace.parent_span_id && (
				<div className='tw-bg-blue-50 tw-p-3 tw-rounded tw-border-l-4 tw-border-blue-400'>
					<div className='tw-flex tw-items-center tw-gap-2'>
						<p className='tw-text-sm tw-text-blue-700'>
							<strong>Child Trace</strong> - Parent Span ID:{' '}
							{trace.parent_span_id}
						</p>
						{isOrphan && (
							<span className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-bg-yellow-100 tw-text-yellow-800'>
								(parent not found)
							</span>
						)}
					</div>
				</div>
			)}
			{trace.children && trace.children.length > 0 && (
				<div className='tw-bg-green-50 tw-p-3 tw-rounded tw-border-l-4 tw-border-green-400'>
					<p className='tw-text-sm tw-text-green-700'>
						<strong>Parent Trace</strong> - Has {trace.children.length} child
						trace{trace.children.length > 1 ? 's' : ''}
					</p>
				</div>
			)}
			<div>
				<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
					<h4 className='tw-font-medium tw-text-gray-700'>System Prompt</h4>
					<div className='tw-flex tw-items-center tw-gap-2'>
						<CopyButton text={trace.system_prompt} label='System Prompt' />
						<button
							type='button'
							onClick={() => setIsSystemPromptExpanded(!isSystemPromptExpanded)}
							className='tw-p-1 tw-text-gray-500 hover:tw-text-gray-700 tw-transition-colors'
							title={isSystemPromptExpanded ? 'Collapse' : 'Expand'}
						>
							{isSystemPromptExpanded ? (
								<ChevronUp className='tw-h-4 tw-w-4' />
							) : (
								<ChevronDown className='tw-h-4 tw-w-4' />
							)}
						</button>
					</div>
				</div>
				<div
					className={`tw-bg-gray-100 tw-rounded tw-text-sm tw-border tw-border-gray-200 tw-transition-all tw-duration-200 ${
						isSystemPromptExpanded ? 'tw-h-96' : 'tw-h-32'
					}`}
				>
					<div
						className='tw-overflow-auto tw-p-3 tw-h-full'
						style={{
							scrollbarWidth: 'thin',
							scrollbarColor: '#cbd5e1 #f1f5f9',
						}}
					>
						{(() => {
							const formatted = formatContentForDisplay(trace.system_prompt);
							return (
								<pre
									className={`tw-whitespace-pre tw-text-xs tw-min-w-0 ${formatted.isParsed ? '' : 'tw-font-mono'}`}
								>
									{formatted.content}
								</pre>
							);
						})()}
					</div>
				</div>
			</div>
			<div>
				<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
					<h4 className='tw-font-medium tw-text-gray-700'>Input Messages</h4>
					<CopyButton
						text={
							typeof trace.input_messages === 'string'
								? trace.input_messages
								: JSON.stringify(trace.input_messages)
						}
						label='Input Messages'
					/>
				</div>
				<div className='tw-bg-gray-100 tw-rounded tw-text-sm tw-border tw-border-gray-200'>
					<div
						className='tw-p-3 tw-max-h-32 tw-overflow-auto'
						style={{
							scrollbarWidth: 'thin',
							scrollbarColor: '#cbd5e1 #f1f5f9',
						}}
					>
						{(() => {
							const formatted = formatContentForDisplay(trace.input_messages);
							return (
								<pre
									className={`tw-whitespace-pre tw-text-xs tw-min-w-0 ${formatted.isParsed ? '' : 'tw-font-mono'}`}
								>
									{formatted.content}
								</pre>
							);
						})()}
					</div>
				</div>
			</div>
			<div>
				<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
					<h4 className='tw-font-medium tw-text-gray-700'>Output Content</h4>
					<CopyButton text={trace.output_content} label='Output Content' />
				</div>
				<div className='tw-bg-gray-100 tw-rounded tw-text-sm tw-border tw-border-gray-200'>
					<div
						className='tw-p-3 tw-max-h-32 tw-overflow-auto'
						style={{
							scrollbarWidth: 'thin',
							scrollbarColor: '#cbd5e1 #f1f5f9',
						}}
					>
						{(() => {
							const formatted = formatContentForDisplay(trace.output_content);
							return (
								<pre
									className={`tw-whitespace-pre tw-text-xs tw-min-w-0 ${formatted.isParsed ? '' : 'tw-font-mono'}`}
								>
									{formatted.content}
								</pre>
							);
						})()}
					</div>
				</div>
			</div>
			<div className='tw-grid tw-grid-cols-2 tw-gap-4'>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>
						Created At
					</h4>
					<div className='tw-text-sm'>{formatDate(trace.created_at)}</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Model</h4>
					<div className='tw-text-sm' title={formatModelName(trace)}>
						{(() => {
							const modelName = formatModelName(trace);
							return modelName.length > 64
								? `${modelName.substring(0, 61)}...`
								: modelName;
						})()}
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>
						Environment
					</h4>
					<div className='tw-text-sm'>
						<span
							className={`tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium ${
								trace.environment === 'production'
									? 'tw-bg-red-100 tw-text-red-800'
									: trace.environment === 'development'
										? 'tw-bg-yellow-100 tw-text-yellow-800'
										: trace.environment === 'bestrev'
											? 'tw-bg-green-100 tw-text-green-800'
											: trace.environment === 'rmws'
												? 'tw-bg-blue-100 tw-text-blue-800'
												: 'tw-bg-gray-100 tw-text-gray-800'
							}`}
						>
							{trace.environment}
						</span>
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>App</h4>
					<div className='tw-text-sm'>
						<span className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium tw-bg-purple-100 tw-text-purple-800'>
							{getAppName(trace)}
						</span>
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Status</h4>
					<div className='tw-text-sm'>
						<span
							className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium'
							style={(() => {
								switch (trace.status) {
									case 'completed':
										return { backgroundColor: '#dcfce7', color: '#166534' }; // green
									case 'error':
									case 'failed':
										return { backgroundColor: '#fee2e2', color: '#dc2626' }; // red
									case 'pending':
										return { backgroundColor: '#fef3c7', color: '#d97706' }; // yellow
									default:
										return { backgroundColor: '#f3f4f6', color: '#1f2937' }; // gray
								}
							})()}
						>
							{trace.status}
						</span>
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Tokens</h4>
					<div className='tw-text-sm'>
						<div className='tw-text-xs tw-text-gray-500'>
							{trace.input_tokens.toLocaleString()} in /{' '}
							{trace.output_tokens.toLocaleString()} out
						</div>
						<div className='tw-font-medium'>
							{trace.total_tokens.toLocaleString()} total
						</div>
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Cost</h4>
					<div className='tw-text-sm tw-font-mono'>
						${Number.parseFloat(trace.total_cost).toFixed(6)}
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>
						Latency (ms)
					</h4>
					<div className='tw-text-sm'>{trace.latency_ms.toLocaleString()}</div>
				</div>
				<div>
					<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
						<h4 className='tw-font-medium tw-text-gray-700'>User ID</h4>
						<CopyButton text={trace.user_id} label='User ID' />
					</div>
					<div className='tw-text-sm tw-font-mono tw-break-all'>
						{trace.user_id}
					</div>
				</div>
				<div>
					<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
						<h4 className='tw-font-medium tw-text-gray-700'>Request ID</h4>
						<CopyButton text={trace.request_id} label='Request ID' />
					</div>
					<div className='tw-text-sm tw-font-mono tw-break-all'>
						{trace.request_id}
					</div>
				</div>
				<div>
					<h4 className='tw-font-medium tw-text-gray-700 tw-mb-2'>Function</h4>
					<div className='tw-text-sm'>
						<span
							className='tw-px-2 tw-py-1 tw-rounded tw-text-xs tw-font-medium'
							style={(() => {
								const normalizedId = trace.function_id
									?.toString()
									.trim()
									.toLowerCase();
								switch (normalizedId) {
									case 'askrev':
										return { backgroundColor: '#dbeafe', color: '#1e40af' }; // blue
									case 'revbot':
										return { backgroundColor: '#fed7aa', color: '#ea580c' }; // orange
									case 'autopilot':
										return { backgroundColor: '#f3e8ff', color: '#7c3aed' }; // purple
									default:
										return { backgroundColor: '#f3f4f6', color: '#1f2937' }; // gray
								}
							})()}
						>
							{trace.function_id}
						</span>
					</div>
				</div>
			</div>
			{trace.error_message && (
				<div>
					<h4 className='tw-font-medium tw-text-red-700 tw-mb-2'>
						Error Message
					</h4>
					<div className='tw-bg-red-50 tw-border tw-border-red-200 tw-p-3 tw-rounded tw-text-sm tw-text-red-800'>
						{trace.error_message}
					</div>
				</div>
			)}
			{trace.metadata && (
				<div>
					<div className='tw-flex tw-justify-between tw-items-center tw-mb-2'>
						<h4 className='tw-font-medium tw-text-gray-700'>Metadata</h4>
						<CopyButton
							text={JSON.stringify(trace.metadata)}
							label='Metadata'
						/>
					</div>
					{(() => {
						const formatted = formatContentForDisplay(trace.metadata);
						return (
							<pre
								className={`tw-bg-gray-100 tw-p-3 tw-rounded tw-text-sm tw-overflow-x-auto tw-whitespace-pre tw-min-w-0 tw-max-w-full ${formatted.isParsed ? '' : 'tw-font-mono'}`}
							>
								{formatted.content}
							</pre>
						);
					})()}
				</div>
			)}
		</div>
	);
};
